import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Search, FolderTree, HelpCircle } from 'lucide-react';
import { categoriesService } from '../../services/categoriesService';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';

export const CategoriesListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesService.getAllCategories(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => categoriesService.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Chyba při mazání kategorie';
      alert(errorMessage);
    },
  });

  const handleDelete = async (id: string, name: string) => {
    if (window.confirm(`Opravdu chcete smazat kategorii "${name}"?`)) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const filteredCategories = categories.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Kategorie</h1>
          <p className="mt-2 text-gray-600">Spravujte kategorie produktů</p>
        </div>
        <Button onClick={() => navigate('/categories/new')}>
          <Plus className="w-4 h-4 mr-2" />
          Nová kategorie
        </Button>
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            type="text"
            placeholder="Hledat podle názvu..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {filteredCategories.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="text-gray-400 mb-4">
            <FolderTree className="w-16 h-16 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Žádné kategorie nenalezeny</h3>
          <p className="text-gray-600 mb-6">
            {search ? 'Zkuste upravit vyhledávání' : 'Začněte vytvořením první kategorie'}
          </p>
          {!search && (
            <Button onClick={() => navigate('/categories/new')}>
              <Plus className="w-4 h-4 mr-2" />
              Vytvořit kategorii
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Název
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SKP kód
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Počet produktů
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Počet podkategorií
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <span
                    className="inline-flex items-center gap-1"
                    title={'„Rozlišuje typ" = u produktů v této kategorii se volí Vestavné / Volně stojící spotřebič. „Bez rozlišení" = typ se neuvádí.'}
                  >
                    Typ
                    <HelpCircle className="w-3.5 h-3.5 text-gray-400" />
                  </span>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Akce
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCategories.map((category) => (
                <tr key={category.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900 max-w-xs truncate" title={category.name}>{category.name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500 max-w-[12rem] truncate" title={category.mssqlCode || undefined}>
                      {category.mssqlCode || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {category._count?.products ?? 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {(category._count?.subcategories ?? 0) === 0 ? (
                      <span className="text-gray-400">0</span>
                    ) : (
                      // Proklik míří rovnou na část s podkategoriemi ve formuláři,
                      // ať se k jejich úpravě nemusí rolovat.
                      <button
                        type="button"
                        onClick={() => navigate(`/categories/${category.id}/edit#podkategorie`)}
                        title={category.subcategories?.map((s) => s.name).join(', ')}
                        className="text-blue-600 hover:text-blue-900 underline decoration-dotted underline-offset-2"
                      >
                        {category._count?.subcategories}
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        category.requiresInstallationType ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {category.requiresInstallationType ? 'Rozlišuje typ' : 'Bez rozlišení'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => navigate(`/categories/${category.id}/edit`)}
                        title="Upravit kategorii a její podkategorie"
                        className="text-blue-600 hover:text-blue-900 p-2 rounded hover:bg-blue-50"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(category.id, category.name)}
                        title="Smazat kategorii – nejde u kategorie, ve které jsou produkty"
                        className="text-red-600 hover:text-red-900 p-2 rounded hover:bg-red-50"
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
